interface PostHogStub {
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  identify?: (distinctId: string, properties?: Record<string, unknown>) => void;
  get_distinct_id?: () => string | undefined;
  get_session_id?: () => string | undefined;
}

declare global {
  interface Window {
    posthog?: PostHogStub;
  }
}

/**
 * Cheap reachability check for `window.posthog.capture`. Callers on hot
 * paths (focusin/input) use this to short-circuit BEFORE building an
 * event payload -- generating uuidv4 + parsing URLs on every keystroke
 * for a call that will no-op is wasted work.
 */
export function posthogCaptureReady(): boolean {
  const ph = typeof window !== 'undefined' ? window.posthog : undefined;
  return !!ph && typeof ph.capture === 'function';
}

/**
 * Safe wrapper around window.posthog.capture. Returns true when the call
 * was dispatched (to real PostHog OR to the pre-init queueing stub that
 * the standard PostHog snippet installs synchronously in <head>). Returns
 * false when no `posthog.capture` is reachable at all (e.g. snippet
 * never ran, ad blocker removed it) or when it threw.
 *
 * Callers with a "once-per-pageview" guard MUST gate their fired flag on
 * this return value -- setting the flag on a no-op call would latch the
 * guard and permanently drop the event for the rest of the pageview even
 * after PostHog becomes reachable on a subsequent interaction.
 */
export function posthogCapture(event: string, properties?: Record<string, unknown>): boolean {
  try {
    const ph = typeof window !== 'undefined' ? window.posthog : undefined;
    if (!ph || typeof ph.capture !== 'function') return false;
    if (properties) ph.capture(event, properties);
    else ph.capture(event);
    return true;
  } catch {
    /* analytics best-effort */
    return false;
  }
}

/** Safe wrapper around window.posthog.identify. */
export function posthogIdentify(distinctId: string, properties?: Record<string, unknown>): void {
  try {
    const ph = typeof window !== 'undefined' ? window.posthog : undefined;
    if (!ph || typeof ph.identify !== 'function') return;
    if (properties && Object.keys(properties).length > 0) {
      ph.identify(distinctId, properties);
    } else {
      ph.identify(distinctId);
    }
  } catch {
    /* analytics best-effort */
  }
}

/** Returns the PostHog distinct_id when available, or undefined. */
export function posthogDistinctId(): string | undefined {
  try {
    const ph = typeof window !== 'undefined' ? window.posthog : undefined;
    if (!ph || typeof ph.get_distinct_id !== 'function') return undefined;
    const id = ph.get_distinct_id();
    return typeof id === 'string' && id ? id : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Returns the PostHog session_id (replay anchor) when available. Lets the
 * backend deep-link from a form submission row directly to the session
 * replay (https://us.posthog.com/project/.../replay/<session_id>).
 */
export function posthogSessionId(): string | undefined {
  try {
    const ph = typeof window !== 'undefined' ? window.posthog : undefined;
    if (!ph || typeof ph.get_session_id !== 'function') return undefined;
    const id = ph.get_session_id();
    return typeof id === 'string' && id ? id : undefined;
  } catch {
    return undefined;
  }
}
